import dayjs from 'dayjs';
import React, { JSX, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CategoryId, Response, ServiceId } from '@/@types/application';
import { PostType } from '@/@types/post';
import ArchivePosts from '../components/ArchivePosts';
import ArchivePostsSkeleton from '../components/ArchivePostsSkeleton';
import Button from '../components/Button';
import Filter from '../components/Filter';
import Layout from '../components/Layout';
import Pagination from '../components/Pagination';
import { CATEGORIES, PAGE_TITLE_SUFFIX, SERVICES } from '../constants';
import useRequest from '../libs/useRequest';
import { BreadcrumbType, Store } from '../store';
import NotFound from './NotFound';

type ArchiveParams = {
  categoryId: CategoryId;
  serviceId?: ServiceId;
};

const Archive: React.FC = (): JSX.Element | null => {
  const { categoryId, serviceId } = useParams<ArchiveParams>();
  const validCategoryId = categoryId && categoryId in CATEGORIES ? (categoryId as CategoryId) : undefined;
  const validServiceId = serviceId && serviceId in SERVICES ? (serviceId as ServiceId) : undefined;
  const { globalState, setGlobalState } = useContext(Store);
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();
  const now = useRef(dayjs());

  const paginate = (pageNumber: number): void => {
    setCurrentPage(pageNumber);
    window.scrollTo(0, 0);
  };

  // INFO: 利用する先のコンポーネントが Selectbox を想定している
  const chagePostsPerPage = (event: React.ChangeEvent<HTMLSelectElement>): void => {
    setGlobalState({ type: 'SET_POSTS_PER_PAGE', payload: Number(event.target.value) });
    setCurrentPage(1);
  };

  // INFO: 利用する先のコンポーネントが Selectbox を想定している
  const changeService = (event: React.ChangeEvent<HTMLSelectElement>): void => {
    const path = event.target.value
      ? `/archive/${validCategoryId}/${event.target.value}/`
      : `/archive/${validCategoryId}/`;
    navigate(path);
    setCurrentPage(1);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: マウント時に1度だけ実行
  useEffect(() => {
    let breadcrumbs: BreadcrumbType[] = [
      { text: 'メンテナンス・障害情報', path: '/' },
      { text: `過去の${validCategoryId ? CATEGORIES[validCategoryId] : ''}情報`, path: `/archive/${validCategoryId}/` },
    ];

    if (!validCategoryId || (serviceId && !validServiceId)) {
      breadcrumbs = [{ text: 'お探しのページが見つかりませんでした', path: '/' }];
      document.title = `ページが見つかりませんでした ${PAGE_TITLE_SUFFIX}`;
    } else if (validServiceId) {
      Array.prototype.push.apply(breadcrumbs, [
        { text: SERVICES[validServiceId], path: `/archive/${validCategoryId}/${validServiceId}/` },
      ]);
      document.title = `${breadcrumbs.slice(-1)[0].text} 過去の${CATEGORIES[validCategoryId]}情報 ${PAGE_TITLE_SUFFIX}`;
    } else {
      document.title = `すべてのサービス 過去の${CATEGORIES[validCategoryId]}情報 ${PAGE_TITLE_SUFFIX}`;
    }

    setGlobalState({ type: 'SET_BREADCRUMBS', payload: breadcrumbs });
    window.scrollTo(0, 0);
  }, [serviceId]);

  const NUMBER_OF_DAYS_TO_GET = 380;
  const endPeriod = useRef(dayjs().startOf('day').subtract(NUMBER_OF_DAYS_TO_GET, 'days'));

  const { data, error } = useRequest<Response<PostType>, Error>(
    validCategoryId
      ? {
          url: 'feeds/',
          params: {
            service: validServiceId,
            type: validCategoryId,
            event_date_end_from: endPeriod.current.format('YYYY-MM-DD HH:mm:ss'),
            ordering: '-event_start',
            limit: 1000,
          },
        }
      : null,
  );

  if (!validCategoryId || (serviceId && !validServiceId)) {
    return <NotFound />;
  }

  if (!data) {
    return (
      <Layout>
        <Filter
          serviceId={serviceId}
          postsPerPage={globalState.postsPerPage}
          changeService={changeService}
          chagePostsPerPage={chagePostsPerPage}
        />
        <ArchivePostsSkeleton postsPerPage={globalState.postsPerPage} />
        <Pagination postsPerPage={globalState.postsPerPage} totalPosts={0} currentPage={0} paginate={paginate} />
        <Button text="現在の状況を見る" path="/" />
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <Filter
          serviceId={serviceId}
          postsPerPage={globalState.postsPerPage}
          changeService={changeService}
          chagePostsPerPage={chagePostsPerPage}
        />
        <div>データ取得に失敗しました。ページを再読み込みしてください。</div>
        <Pagination postsPerPage={globalState.postsPerPage} totalPosts={0} currentPage={0} paginate={paginate} />
        <Button text="現在の状況を見る" path="/" />
      </Layout>
    );
  }

  if (data) {
    const archivePosts = data.results.filter((post) => +post.event_end < now.current.unix());
    const indexOfLastPost = currentPage * globalState.postsPerPage;
    const indexOfFirstPost = indexOfLastPost - globalState.postsPerPage;
    const currentPosts = archivePosts.slice(indexOfFirstPost, indexOfLastPost);

    return (
      <Layout>
        <Filter
          serviceId={serviceId}
          postsPerPage={globalState.postsPerPage}
          changeService={changeService}
          chagePostsPerPage={chagePostsPerPage}
        />
        <ArchivePosts posts={currentPosts} />
        <Pagination
          postsPerPage={globalState.postsPerPage}
          totalPosts={archivePosts.length}
          currentPage={currentPage}
          paginate={paginate}
        />
        <Button text="現在の状況を見る" path="/" />
      </Layout>
    );
  }

  return null;
};

export default Archive;
